import { QueryBuilderDnD } from '@react-querybuilder/dnd'
import { useEffect, useMemo, useRef, useState } from 'react'
import * as ReactDnD from 'react-dnd'
import * as ReactDndHtml5Backend from 'react-dnd-html5-backend'
import {
  ActionProps,
  Field,
  FieldSelectorProps,
  OperatorSelectorProps,
  QueryBuilder as QB,
  RuleGroupType,
  ValueEditorProps,
} from 'react-querybuilder'
import { useShallow } from 'zustand/react/shallow'

import { ChevronDownIcon, CloseIcon, RecyclingBinIcon } from '@ui/components/icons'
import { extractFieldSetFromStepPredicates, toJSONataTranslatableField, toStepPredicate } from '@ui/lib/helpers/keys'
import { getAttributeValues } from '@ui/lib/helpers/tableHelpers'
import { useTelemetryStore } from '@ui/stores'
import { useNodeAndEdgeStore } from '@ui/stores/pipelinesWorkflow'
import { useQueryBuilderStore } from '@ui/stores/pipelinesWorkflow/QueryBuilderStore'
import clsx from 'clsx'

import './QueryBuilder.css'

interface QueryBuilderProps {
  closePanel: () => void
}

export const QueryBuilder = ({ closePanel }: QueryBuilderProps) => {
  const [currentField, setCurrentField] = useState<string | null>(null)

  const { queryConfig, updateQuery, stepId, inputId } = useQueryBuilderStore(
    useShallow((state) => ({
      queryConfig: state.queryConfig,
      updateQuery: state.updateQuery,
      stepId: state.stepId,
      inputId: state.inputId,
    }))
  )

  const { uniqueKeysLogs, uniqueKeysSpans, dataSource, logs, spans } = useTelemetryStore(
    useShallow((state) => ({
      logs: state.logs,
      uniqueKeysLogs: state.uniqueKeysLogs,
      uniqueKeysSpans: state.uniqueKeysSpans,
      dataSource: state.dataSource,
      spans: state.spans,
    }))
  )

  const { updateNodeStepPredicate, nodes } = useNodeAndEdgeStore(
    useShallow((state) => ({
      updateNodeStepPredicate: state.updateNodeStepPredicate,
      nodes: state.nodes,
    }))
  )

  // console.log('stepId', stepId)
  // console.log('inputId', inputId)

  // We need to get the values for the field that is currently selected
  // Better than using handleUpdateQuery because we need to get the values for the field that is currently selected
  const getValues = (field: string) => {
    setCurrentField(field)
    return []
  }

  const fields = useMemo(() => {
    const generateFieldsFromAttributes = (): Field[] => {
      const uniqueKeys = dataSource === 'logs' ? uniqueKeysLogs : uniqueKeysSpans
      // We want to get all the fields from the firehose and the step predicates because we
      // want to  be able to query all the fields that are available to the user.
      const fieldsFromFirehose = new Set(uniqueKeys.map((key) => key.key))
      const fieldsFromStepPredicates = extractFieldSetFromStepPredicates(nodes)
      const allFields = Array.from(new Set([...fieldsFromFirehose, ...fieldsFromStepPredicates]))

      // Sort fields alphabetically
      return allFields.sort((a, b) => (a < b ? -1 : 1)).map(toJSONataTranslatableField)
    }
    return generateFieldsFromAttributes()
  }, [dataSource, uniqueKeysLogs, uniqueKeysSpans, nodes])

  const handleUpdateQuery = (ruleGroup: RuleGroupType) => {
    updateQuery(ruleGroup)
    const stepPredicate = toStepPredicate(ruleGroup)
    updateNodeStepPredicate(stepId, stepPredicate)
  }

  return (
    <div className="flex size-full flex-col items-center justify-start">
      <div className="sticky top-0 z-10 flex w-full items-center justify-between border-b border-gray-300 bg-white p-4">
        <p className="text-sm font-semibold">Query Builder</p>
        <CloseIcon
          className="h-3 w-3 cursor-pointer stroke-gray-300 transition-all duration-300 ease-in-out hover:scale-110 hover:stroke-gray-500"
          onClick={closePanel}
        />
      </div>
      <div className="size-full overflow-y-auto">
        <QueryBuilderDnD dnd={{ ...ReactDnD, ...ReactDndHtml5Backend }}>
          <QB
            query={queryConfig[`${stepId}-${inputId}`]?.query ?? { combinator: 'and', rules: [] }}
            onQueryChange={handleUpdateQuery}
            fields={fields}
            getValues={getValues}
            context={{
              currentField,
              logs,
              spans,
              dataSource,
            }}
            controlElements={{
              fieldSelector: FieldSelector,
              operatorSelector: OperatorSelector,
              valueEditor: ValueEditor,
              removeRuleAction: RemoveRuleAction,
              removeGroupAction: RemoveGroupAction,
            }}
          />
        </QueryBuilderDnD>
      </div>
    </div>
  )
}

const FieldSelector = ({ options, value, handleOnChange }: FieldSelectorProps) => {
  const fieldSelectorRef = useRef<HTMLDivElement>(null)

  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const filteredOptions = useMemo(
    () =>
      options.filter((option) => {
        const trimmedSearch = searchTerm.trim().toLowerCase()
        return trimmedSearch ? option.label.toLowerCase().includes(trimmedSearch) : false
      }),
    [options, searchTerm]
  )

  useEffect(() => {
    setSearchTerm(value ?? '')
  }, [value])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (fieldSelectorRef.current && !fieldSelectorRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  return (
    <div className="relative w-full" ref={fieldSelectorRef}>
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value)
          handleOnChange(e.target.value)
        }}
        onFocus={() => setIsOpen(true)}
        placeholder="Search fields..."
        className="field-selector h-8 w-full rounded border border-gray-200 p-1 text-xs placeholder:text-xs focus:border-primary-500 focus:outline-none focus:ring-0"
      />

      {isOpen && filteredOptions.length > 0 && (
        <div className="field-selector absolute z-10 mt-1 max-h-40 w-auto overflow-y-auto rounded border bg-white shadow-lg">
          {filteredOptions.map((option, index) => (
            <div
              key={index}
              className={clsx('cursor-pointer p-1.5 text-xs hover:bg-primary-100', {
                'bg-gray-100': value === option.label,
              })}
              onClick={() => {
                setIsOpen(false)
                setSearchTerm(option.label)
                handleOnChange(option.label)
              }}>
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const OperatorSelector = ({ options, value, handleOnChange }: OperatorSelectorProps) => {
  const operatorSelectorRef = useRef<HTMLDivElement>(null)

  const [showOptions, setShowOptions] = useState(false)
  const [selectedOperator, setSelectedOperator] = useState(value)
  const operators = options.map((option) => ({
    label: option.label,
    value: option.label,
  }))

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (operatorSelectorRef.current && !operatorSelectorRef.current.contains(e.target as Node)) {
        setShowOptions(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [showOptions])

  return (
    <div className="relative w-96" ref={operatorSelectorRef}>
      <div
        tabIndex={0}
        onClick={() => setShowOptions(!showOptions)}
        className={clsx(
          'flex h-8 w-full cursor-pointer items-center justify-between rounded border border-gray-200 bg-white p-2 placeholder:text-xs',
          'focus:border-primary-500 focus:outline-none focus:ring-0',
          {
            'border-primary-500': showOptions,
          }
        )}>
        <p className="text-sm font-semibold">{selectedOperator}</p>
        <ChevronDownIcon
          className={clsx('h-4 w-4 stroke-gray-900 transition-all duration-300 ease-in-out', {
            'rotate-180': showOptions,
          })}
        />
      </div>
      {showOptions && (
        <div className="absolute z-10 mt-1 max-h-40 w-full overflow-y-auto rounded border bg-white shadow-sm">
          {operators.map((operator) => (
            <div
              key={operator.value}
              className={clsx('w-full cursor-pointer p-1.5 hover:bg-primary-100', {
                'bg-gray-100': selectedOperator === operator.value,
              })}
              onClick={() => {
                setSelectedOperator(operator.value)
                handleOnChange(operator.value)
              }}>
              <p className="text-xs font-medium">{operator.label}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const ValueEditor = ({ handleOnChange, context, value }: ValueEditorProps) => {
  const { currentField, logs, spans, dataSource } = context

  const valueEditorRef = useRef<HTMLDivElement>(null)

  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [filteredValues, setFilteredValues] = useState<string[]>([])
  const [formattedValues, setFormattedValues] = useState<string[]>([])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (valueEditorRef.current && !valueEditorRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('click', handleClickOutside)
    }
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [isOpen])

  useEffect(() => {
    setSearchTerm(value ?? '')
  }, [value])

  // Create the values for the field that is currently selected
  useEffect(() => {
    if (!currentField || (dataSource === 'logs' && !logs) || (dataSource === 'spans' && !spans)) return

    switch (dataSource) {
      case 'logs': {
        const values = Array.from(getAttributeValues(logs, currentField ?? ''))
        setFormattedValues(values.map((value) => String(value)))
        break
      }
      case 'spans': {
        const values = Array.from(getAttributeValues(spans, currentField ?? ''))
        setFormattedValues(values.map((value) => String(value)))
        break
      }
    }
  }, [currentField, logs, spans, dataSource])

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsOpen(true)
    // Update the local state
    setSearchTerm(e.target.value)
    // Update the query builder state
    handleOnChange(e.target.value)
    // Filter the values based on the search term
    const trimmedSearch = e.target.value.trim().toLowerCase()
    const filteredValues = formattedValues.filter((value) => {
      return trimmedSearch ? value.toLowerCase().includes(trimmedSearch) : false
    })
    setFilteredValues(filteredValues)
  }

  return (
    <div className="relative w-full" ref={valueEditorRef}>
      <input
        type="text"
        value={searchTerm}
        onChange={handleSearch}
        onFocus={() => setIsOpen(true)}
        className="h-8 w-full rounded border border-gray-200 p-1 text-xs placeholder:text-xs focus:border-primary-500 focus:outline-none focus:ring-0"
      />
      {isOpen && filteredValues.length > 0 && (
        <div className="absolute z-10 mt-1 max-h-40 w-full overflow-y-auto rounded border border-gray-200 bg-white shadow-lg">
          {filteredValues.map((value) => (
            <div
              key={Array.isArray(value) ? value.join(',') : value}
              className={clsx('cursor-pointer p-1.5 text-xs hover:bg-primary-100', {
                'bg-gray-100': value === searchTerm,
              })}
              onClick={() => {
                setIsOpen(false)
                const displayValue = Array.isArray(value) ? value.join(', ') : value
                handleOnChange(String(displayValue))
                setSearchTerm(String(displayValue))
              }}>
              {Array.isArray(value) ? value.join(', ') : String(value)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const RemoveRuleAction = ({ handleOnClick }: ActionProps) => {
  return (
    <div
      title="Remove Rule"
      className="flex cursor-pointer items-center justify-center rounded p-2 transition-all duration-300 ease-in-out hover:bg-gray-200"
      onClick={handleOnClick}>
      <RecyclingBinIcon className="h-4 w-4 fill-gray-500" />
    </div>
  )
}

const RemoveGroupAction = ({ handleOnClick }: ActionProps) => {
  return (
    <div
      title="Remove Group"
      className="flex cursor-pointer items-center justify-center rounded p-2 transition-all duration-300 ease-in-out hover:bg-gray-200"
      onClick={handleOnClick}>
      <RecyclingBinIcon className="h-4 w-4 fill-gray-500" />
    </div>
  )
}
